import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../../api.js';
import { useAuth } from '../../auth.jsx';
import Field from '../../components/Field.jsx';
import Alert from '../../components/ui/Alert.jsx';
import Modal, { ModalActions } from '../../components/ui/Modal.jsx';
import RowMenu from '../../components/ui/RowMenu.jsx';
import { EmptyState, Loading, PageHeader, Pill, Section } from '../../components/ui/index.jsx';
import { KindField, ScopeField, coversText, scopeFromKey } from './peopleShared.jsx';
import { BackModal, CoverModal, EditPersonModal, LeftModal, RemovePersonModal } from './personModals.jsx';
import { checkEmail, checkPhone, checkRequired } from '../../utils/validate.js';
import { fmtDay } from '../../utils/format.js';

function AddPersonModal({ base, data, onClose, onAdded }) {
  const [form, setForm] = useState({
    first_name: '', last_name: '', job_title: '', kind: 'employee', role_label: '', scope: 'company', email: '', phone: '', send_confirmation: true,
  });
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (k, v) => { setForm({ ...form, [k]: v }); if (errors[k]) setErrors({ ...errors, [k]: undefined }); };
  const blur = (k, problem) => setErrors((e) => ({ ...e, [k]: problem || undefined }));

  const save = async () => {
    const found = Object.fromEntries(Object.entries({
      first_name: checkRequired(form.first_name, 'their first name', { max: 100 }),
      last_name: checkRequired(form.last_name, 'their last name', { max: 100 }),
      email: checkEmail(form.email, { required: false })
        || (!form.email.trim() && !form.phone.trim() ? 'Add their email address, their phone number, or both.' : ''),
      phone: checkPhone(form.phone, { required: false }),
    }).filter(([, m]) => m));
    if (Object.keys(found).length) { setErrors(found); return; }
    setBusy(true); setError('');
    try {
      const res = await api(base, { method: 'POST', body: { ...form, scope: scopeFromKey(form.scope) } });
      onAdded(res);
    } catch (err) {
      setErrors(err.data?.fields || {});
      setError(err.data?.fields ? '' : err.message);
      setBusy(false);
    }
  };

  return (
    <Modal title="Add a person" onClose={onClose} onSubmit={save} busy={busy} size="lg"
      description="Someone who sends email or calls people on your behalf. They’re never shown to the public: a check of their address just says it’s yours."
      footer={<ModalActions submitLabel="Add person" busyLabel="Adding…" busy={busy} onCancel={onClose} />}>
      {error && <Alert tone="danger">{error}</Alert>}
      <div className="row-2">
        <Field label="First name" required value={form.first_name} onChange={(e) => set('first_name', e.target.value)} error={errors.first_name} maxLength={100} autoComplete="off" />
        <Field label="Last name" required value={form.last_name} onChange={(e) => set('last_name', e.target.value)} error={errors.last_name} maxLength={100} autoComplete="off" />
      </div>
      <div className="row-2">
        <Field label="Job title" optional maxLength={100} value={form.job_title} onChange={(e) => set('job_title', e.target.value)} placeholder="Exhibitor sales manager" />
        <KindField value={form.kind} onChange={(v) => set('kind', v)} kinds={data.kinds} error={errors.kind} />
      </div>
      <div className="row-2">
        <Field label="Email address" optional type="email" autoCapitalize="none" autoComplete="off" value={form.email} onChange={(e) => set('email', e.target.value)}
          onBlur={() => form.email.trim() && blur('email', checkEmail(form.email))} error={errors.email}
          hint={!errors.email ? 'Any address they send from, even Gmail or an agency’s.' : undefined} />
        <Field label="Phone number" optional type="tel" inputMode="tel" autoComplete="off" value={form.phone} onChange={(e) => set('phone', e.target.value)}
          onBlur={() => form.phone.trim() && blur('phone', checkPhone(form.phone))} error={errors.phone}
          hint={!errors.phone ? 'Include the country code if it’s outside the UK.' : undefined} />
      </div>
      <p className="muted small field-note">Add at least one: an email address, a phone number, or both.</p>
      <ScopeField value={form.scope} onChange={(v) => set('scope', v)} scopes={data.scopes} companyName={data.organisation.name} error={errors.scope} />
      <label className="checkbox">
        <input type="checkbox" checked={form.send_confirmation} onChange={(e) => set('send_confirmation', e.target.checked)} />
        <span>Email them to confirm the address is theirs <span className="muted small">(it only counts once they do, unless it&rsquo;s on your verified domain)</span></span>
      </label>
    </Modal>
  );
}

const CONFIRM_NOTICE = {
  sent: 'We’ve emailed them to confirm their address. It counts once they do.',
  failed: 'Added, but the confirmation email couldn’t be sent. Try again from their page.',
  skipped: 'Added. Their address won’t count until they confirm it — send the email from their page when ready.',
  not_needed: 'Added. Their address is on your verified domain, so it counts already.',
};

export default function People() {
  const { organisation: current } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState(location.state?.notice || '');
  const [action, setAction] = useState(null);   // { kind: edit|role|cover|left|back|remove, person, cover? }
  const base = current ? `/portal/organisations/${current.id}/people` : '';

  const load = useCallback(async () => {
    if (!current) return;
    try { setData(await api(base)); } catch (err) { setError(err.message); }
  }, [base, current]);
  useEffect(() => { load(); }, [load]);

  const done = async (text) => { setAction(null); if (text) setNotice(text); await load(); };
  const personBase = (p) => `${base}/${p.id}`;
  // One role: change it here. Several: their page lists them all. None: add one.
  const changeRole = (p) => (p.covers.length === 1 ? setAction({ kind: 'role', person: p, cover: p.covers[0] })
    : p.covers.length === 0 ? setAction({ kind: 'cover', person: p }) : navigate(`/company/people/${p.id}`));

  if (error) return <div className="page"><PageHeader title="People" /><Alert tone="danger">{error}</Alert></div>;
  if (!data) return <div className="page"><Loading /></div>;

  const addButton = <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>Add person</button>;
  return (
    <div className="page">
      <PageHeader title="People"
        subtitle="Everyone who sends email or calls on your behalf, including freelancers and agency staff. Only owners and admins of your company can see this list."
        actions={data.people.length > 0 && addButton} />

      {notice && <Alert tone="success">{notice}</Alert>}
      {data.people.length > 0 && (
        <p className="muted small">
          Changed job? Use <strong>Change role</strong>. Left? Use <strong>They&rsquo;ve left</strong>: it keeps them listed so anything still sent from
          their addresses is flagged. <strong>Remove</strong> is for someone added by mistake.
        </p>
      )}

      <Section flush={data.people.length > 0}>
        {data.people.length === 0 ? (
          <EmptyState title="Nobody listed yet" action={addButton}>
            List the people who contact exhibitors and attendees. Then a check of one of their addresses says it&rsquo;s yours — and
            someone using their name from another address is caught.
          </EmptyState>
        ) : (
          <div className="table-scroll">
            <table className="table table-stack">
              <thead><tr><th>Name</th><th>Covers</th><th>Addresses</th><th>Status</th><th><span className="sr-only">Actions</span></th></tr></thead>
              <tbody>
                {data.people.map((p) => (
                  <tr key={p.id}>
                    <td data-label="Name">
                      <Link className="strong-link" to={`/company/people/${p.id}`}>{p.name}</Link>
                      {p.job_title && <div className="muted small">{p.job_title}</div>}
                    </td>
                    <td data-label="Covers">{p.covers.map(coversText).join(', ') || <span className="muted">Nothing</span>}</td>
                    <td data-label="Addresses">
                      {p.emails > 0 && <span>{p.emails_verified}/{p.emails} email{p.emails === 1 ? '' : 's'} confirmed</span>}
                      {p.emails > 0 && p.phones > 0 && ', '}
                      {p.phones > 0 && <span>{p.phones} phone{p.phones === 1 ? '' : 's'}</span>}
                    </td>
                    <td data-label="Status">{p.status === 'left' ? <Pill>Left {fmtDay(p.left_on)}</Pill> : p.emails_verified < p.emails ? <Pill tone="warn">Waiting for them to confirm</Pill> : <Pill tone="good">Active</Pill>}</td>
                    <td className="right row-actions">
                      <RowMenu ariaLabel={`Manage ${p.name}`} items={[
                        { label: 'View', onClick: () => navigate(`/company/people/${p.id}`) },
                        { label: 'Edit details', onClick: () => setAction({ kind: 'edit', person: p }) },
                        { label: p.covers.length > 1 ? 'Change roles…' : p.covers.length ? 'Change role' : 'Add what they cover', onClick: () => changeRole(p) },
                        { label: 'They’ve left', onClick: () => setAction({ kind: 'left', person: p }), hidden: p.status === 'left' },
                        { label: 'They’re back', onClick: () => setAction({ kind: 'back', person: p }), hidden: p.status !== 'left' },
                        { label: 'Remove', danger: true, onClick: () => setAction({ kind: 'remove', person: p }) },
                      ]} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {action?.kind === 'edit' && <EditPersonModal base={personBase(action.person)} person={action.person} onClose={() => setAction(null)} onSaved={() => done(`${action.person.name}’s details are saved.`)} />}
      {action?.kind === 'role' && <CoverModal base={personBase(action.person)} data={data} cover={action.cover} onClose={() => setAction(null)} onSaved={() => done(`${action.person.name}’s role is updated.`)} />}
      {action?.kind === 'cover' && <CoverModal base={personBase(action.person)} data={data} onClose={() => setAction(null)} onSaved={() => done('Added.')} />}
      {action?.kind === 'left' && <LeftModal base={personBase(action.person)} person={action.person} onClose={() => setAction(null)} onSaved={() => done(`${action.person.first_name}’s addresses no longer count from that date.`)} />}
      {action?.kind === 'back' && <BackModal base={personBase(action.person)} person={action.person} onClose={() => setAction(null)} onSaved={() => done(`${action.person.first_name}’s addresses count again.`)} />}
      {action?.kind === 'remove' && <RemovePersonModal base={personBase(action.person)} person={action.person} onClose={() => setAction(null)} onRemoved={() => done(`${action.person.name} has been removed.`)} />}

      {adding && (
        <AddPersonModal base={base} data={data} onClose={() => setAdding(false)}
          onAdded={(res) => navigate(`/company/people/${res.person.id}`, { state: { notice: CONFIRM_NOTICE[res.confirmation] } })} />
      )}
    </div>
  );
}
